import {
  Component, OnInit, OnDestroy, Inject, inject, Injector,
  ChangeDetectionStrategy, ChangeDetectorRef, signal
} from '@angular/core';
import { Router } from '@angular/router';
import { CommonModule } from '@angular/common';
import { Subscription } from 'rxjs';

import { MatDialogModule, MatDialogRef, MatDialog, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatDividerModule } from '@angular/material/divider';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';

import { AgGridModule } from 'ag-grid-angular';

import { SavedReport } from '../../models/saved-report.model';
import { ENTITY_REGISTRY } from '../../entity-registry/entity-registry';
import { SendReportDialogComponent } from '../send-report-dialog/send-report-dialog.component';
import { applyDateRangeFilter } from '../date-range-filter/date-range-filter.component';

export interface ReportPreviewDialogData {
  report: SavedReport;
}

@Component({
  selector: 'app-report-preview-dialog',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    CommonModule,
    AgGridModule,
    MatDialogModule,
    MatButtonModule,
    MatIconModule,
    MatDividerModule,
    MatTooltipModule,
    MatProgressSpinnerModule
  ],
  template: `
    <!-- Header -->
    <div class="preview-header">
      <div class="header-title-row">
        <div class="header-left">
          <mat-icon class="header-icon">assessment</mat-icon>
          <div class="header-text">
            <h2 class="report-title">{{ data.report.name }}</h2>
            <span class="entity-badge">
              <mat-icon class="badge-icon">{{ entityIcon }}</mat-icon>
              {{ entityLabel }}
            </span>
          </div>
        </div>
        <button mat-icon-button (click)="close()" matTooltip="Close">
          <mat-icon>close</mat-icon>
        </button>
      </div>

      <!-- Stats Row -->
      <div class="stats-row">
        @if (activeFilterCount() > 0) {
          <div class="stat-chip filters-chip">
            <mat-icon>filter_list</mat-icon>
            <span>{{ activeFilterCount() }} {{ activeFilterCount() === 1 ? 'Filter' : 'Filters' }} Active</span>
          </div>
        }
        <div class="stat-chip results-chip">
          <mat-icon>table_rows</mat-icon>
          <span>{{ rowCount() }} {{ rowCount() === 1 ? 'Result' : 'Results' }}</span>
        </div>
      </div>
    </div>

    <mat-divider></mat-divider>

    <!-- Grid -->
    <div class="preview-grid-container">
      @if (isLoading()) {
        <div class="grid-loading-overlay">
          <mat-spinner diameter="50"></mat-spinner>
          <p>Loading report data...</p>
        </div>
      }

      <ag-grid-angular
        [style.visibility]="isLoading() ? 'hidden' : 'visible'"
        style="width: 100%; height: 100%"
        class="ag-theme-balham"
        [defaultColDef]="defaultColDef"
        [rowData]="rowData"
        [columnDefs]="columnDefs"
        [animateRows]="true"
        [pagination]="true"
        [paginationPageSize]="50"
        [paginationPageSizeSelector]="[25, 50, 100, 500]"
        (gridReady)="onGridReady($event)"
        (filterChanged)="onFilterChanged()"
      >
      </ag-grid-angular>
    </div>

    <mat-divider></mat-divider>

    <!-- Footer Actions -->
    <div class="preview-footer">
      <div class="footer-left">
        <button mat-raised-button color="primary" (click)="openInBuilder()">
          <mat-icon>edit</mat-icon> Open in Builder
        </button>
      </div>
      <div class="footer-right">
        <button mat-stroked-button (click)="sendReportNow()" matTooltip="Email this report">
          <mat-icon>send</mat-icon> Send
        </button>
        <button mat-stroked-button (click)="exportCSV()" matTooltip="Download as CSV">
          <mat-icon>table_chart</mat-icon> CSV
        </button>
        <button mat-stroked-button (click)="exportExcel()" matTooltip="Download as Excel">
          <mat-icon>grid_on</mat-icon> Excel
        </button>
        <button mat-button (click)="close()">Close</button>
      </div>
    </div>
  `,
  styles: [`
    :host {
      display: flex;
      flex-direction: column;
      height: 85vh;
      width: 90vw;
      max-width: 1400px;
    }

    /* Header */
    .preview-header {
      padding: 20px 24px 12px;
    }

    .header-title-row {
      display: flex;
      align-items: flex-start;
      justify-content: space-between;
    }

    .header-left {
      display: flex;
      align-items: center;
      gap: 16px;
    }

    .header-icon {
      font-size: 36px;
      width: 36px;
      height: 36px;
      color: #1976d2;
    }

    .header-text {
      display: flex;
      flex-direction: column;
      gap: 4px;
    }

    .report-title {
      margin: 0;
      font-size: 22px;
      font-weight: 500;
      color: #212121;
    }

    .entity-badge {
      display: inline-flex;
      align-items: center;
      gap: 4px;
      font-size: 13px;
      color: #666;
    }

    .badge-icon {
      font-size: 16px;
      width: 16px;
      height: 16px;
    }

    /* Stats Row */
    .stats-row {
      display: flex;
      gap: 12px;
      margin-top: 12px;
    }

    .stat-chip {
      display: inline-flex;
      align-items: center;
      gap: 6px;
      padding: 6px 14px;
      border-radius: 20px;
      font-size: 14px;
      font-weight: 500;
    }

    .stat-chip mat-icon {
      font-size: 18px;
      width: 18px;
      height: 18px;
    }

    .filters-chip {
      background: #fff3e0;
      color: #e65100;
      border: 1px solid #ffcc80;
    }

    .results-chip {
      background: #e3f2fd;
      color: #1565c0;
      border: 1px solid #90caf9;
    }

    /* Grid */
    .preview-grid-container {
      flex: 1;
      position: relative;
      overflow: hidden;
    }

    .grid-loading-overlay {
      position: absolute;
      top: 0;
      left: 0;
      right: 0;
      bottom: 0;
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      background: rgba(255, 255, 255, 0.9);
      z-index: 10;
    }

    .grid-loading-overlay p {
      margin-top: 12px;
      color: #666;
    }

    /* Footer */
    .preview-footer {
      display: flex;
      align-items: center;
      justify-content: space-between;
      padding: 12px 24px;
    }

    .footer-left {
      display: flex;
      gap: 8px;
    }

    .footer-right {
      display: flex;
      align-items: center;
      gap: 8px;
    }
  `]
})
export class ReportPreviewDialogComponent implements OnInit, OnDestroy {

  private router = inject(Router);
  private injector = inject(Injector);
  private matDialog = inject(MatDialog);
  private cdr = inject(ChangeDetectorRef);

  // Signals
  isLoading = signal<boolean>(true);
  activeFilterCount = signal<number>(0);
  rowCount = signal<number>(0);

  // Entity info
  entityLabel = '';
  entityIcon = '';

  // AG Grid
  gridApi: any = null;
  rowData: any[] = [];
  columnDefs: any[] = [];
  defaultColDef = {
    filter: true,
    floatingFilter: false,
    resizable: true,
    sortable: true,
  };

  private dataSubscription: Subscription | null = null;

  constructor(
    public dialogRef: MatDialogRef<ReportPreviewDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: ReportPreviewDialogData
  ) {}

  ngOnInit(): void {
    const report = this.data.report;
    const entry = ENTITY_REGISTRY[report.entityType];

    if (entry) {
      this.entityLabel = entry.label;
      this.entityIcon = entry.icon;

      // Run setup if needed
      if (entry.setup) {
        entry.setup(this.injector);
      }

      // Set columns — all columns, state will be applied after grid ready.
      // Date columns MUST get the same filter swap the report builder applies,
      // or a saved date-filter model written by the builder is unreadable here
      // and the filter silently does nothing when the report is actually run.
      this.columnDefs = applyDateRangeFilter(entry.columns);

      // Subscribe to entity data
      const service = this.injector.get(entry.serviceType);
      const observable = service[entry.observableName];

      if (observable) {
        this.dataSubscription = observable.subscribe((data: any[]) => {
          this.rowData = data || [];
          this.rowCount.set(data?.length || 0);
          this.isLoading.set(false);
          this.cdr.markForCheck();
        });
      } else {
        this.isLoading.set(false);
      }
    } else {
      this.entityLabel = report.entityType;
      this.isLoading.set(false);
    }

    // Pre-calculate filter count from saved state
    if (report.gridState?.filterModel) {
      this.activeFilterCount.set(Object.keys(report.gridState.filterModel).length);
    }
  }

  ngOnDestroy(): void {
    this.dataSubscription?.unsubscribe();
  }

  onGridReady(params: any): void {
    this.gridApi = params.api;

    const report = this.data.report;

    // Apply saved column state
    if (report.gridState?.columnState?.length > 0) {
      setTimeout(() => {
        this.gridApi.applyColumnState({
          state: report.gridState.columnState,
          applyOrder: true
        });

        // Apply saved filter model
        if (report.gridState.filterModel && Object.keys(report.gridState.filterModel).length > 0) {
          this.gridApi.setFilterModel(report.gridState.filterModel);
        }

        this.updateCounts();
        this.cdr.markForCheck();
      }, 100);
    }
  }

  onFilterChanged(): void {
    this.updateCounts();
  }

  private updateCounts(): void {
    if (!this.gridApi) return;

    const filterModel = this.gridApi.getFilterModel();
    this.activeFilterCount.set(filterModel ? Object.keys(filterModel).length : 0);
    this.rowCount.set(this.gridApi.getDisplayedRowCount());
    this.cdr.markForCheck();
  }

  exportCSV(): void {
    if (this.gridApi) {
      this.gridApi.exportDataAsCsv({
        fileName: `${this.data.report.name}.csv`
      });
    }
  }

  exportExcel(): void {
    if (this.gridApi) {
      this.gridApi.exportDataAsExcel({
        fileName: `${this.data.report.name}.xlsx`
      });
    }
  }

  openInBuilder(): void {
    this.dialogRef.close();
    this.router.navigate(['/securehome/report-builder', this.data.report.reportId]);
  }

  sendReportNow(): void {
    this.matDialog.open(SendReportDialogComponent, {
      data: {
        reportName: this.data.report.name,
        gridApi: this.gridApi
      }
    });
  }

  close(): void {
    this.dialogRef.close();
  }
}
